import { forwardRef, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import InsertTagMenu from "./insert-tag-menu";
import { CopyContext } from "../services/copy-tags-api";
import { insertAtCursor } from "../utils/insert-at-cursor";

type Props = {
  value: string;
  onChange: (value: string) => void;
  context: CopyContext;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
};

// A text field with an "Insert price" menu that drops the chosen tag at the
// caret. Works for plain text and for JSON boxes: a tag holds no quotes, so it
// is safe inside a JSON string.
const TagTextField = forwardRef<HTMLDivElement, Props>(function TagTextField(
  { value, onChange, context, multiline, rows, placeholder, className, disabled },
  ref,
) {
  const field = useRef<HTMLInputElement & HTMLTextAreaElement>(null);

  const insert = (tag: string) => {
    const el = field.current;
    // Keep the box (and the page) exactly where the admin left them — focusing
    // and moving the caret would otherwise scroll both to the caret.
    const boxScroll = el?.scrollTop ?? 0;
    const next = insertAtCursor(el, value ?? "", tag);
    onChange(next.value);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus({ preventScroll: true });
      el.setSelectionRange(next.caret, next.caret);
      el.scrollTop = boxScroll;
    });
  };

  return (
    <div ref={ref} className="space-y-1">
      <div className="flex justify-end">
        <InsertTagMenu context={context} onInsert={insert} disabled={disabled} />
      </div>
      {multiline ? (
        <Textarea
          ref={field}
          rows={rows ?? 4}
          className={className}
          placeholder={placeholder}
          value={value ?? ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <Input
          ref={field}
          className={cn("h-8 text-sm", className)}
          placeholder={placeholder}
          value={value ?? ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
});

export default TagTextField;
